import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterFormPage from "../common/MasterFormPage";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import {
  createDistrict,
  DISTRICT_UPDATE_SUPPORTED,
  getDistrictById,
  updateDistrict,
} from "../../services/districtService";
import {
  extractApiErrorMessage,
  getDefaultUserState,
} from "../../utils/masterHelpers";
import "./Districts.css";

const createInitialValues = () => ({
  code: "",
  name: "",
  state: getDefaultUserState(),
});

export default function DistrictForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: "", open: false, severity: "success" });
  const [values, setValues] = useState(createInitialValues);
  const isEditMode = mode === "edit";
  const readOnly = isEditMode && !DISTRICT_UPDATE_SUPPORTED;

  const loadDistrict = useCallback(async () => {
    if (!isEditMode) {
      return;
    }

    setLoading(true);

    try {
      if (!id) {
        setToast({
          message: "District ID is missing.",
          open: true,
          severity: "error",
        });
        return;
      }

      const district = await getDistrictById(id);

      if (!district) {
        setToast({
          message: "District not found.",
          open: true,
          severity: "error",
        });
        return;
      }

      setValues({
        code: district.code || "",
        name: district.name || "",
        state: district.state || "",
      });
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(apiError, "Failed to load district details."),
        open: true,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    loadDistrict();
  }, [loadDistrict]);

  const fields = useMemo(
    () => [
      {
        name: "code",
        label: "Code",
        placeholder: "Enter district code",
        required: true,
        error: errors.code,
      },
      {
        name: "name",
        label: "Name",
        placeholder: "Enter district name",
        required: true,
        error: errors.name,
      },
      {
        name: "state",
        label: "State",
        placeholder: "Enter state name",
        required: true,
        error: errors.state,
      },
    ],
    [errors.code, errors.name, errors.state]
  );

  const validate = () => {
    const nextErrors = {};

    if (!values.code.trim()) {
      nextErrors.code = "District code is required.";
    }

    if (!values.name.trim()) {
      nextErrors.name = "District name is required.";
    }

    if (!values.state.trim()) {
      nextErrors.state = "State is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        code: values.code.trim(),
        name: values.name.trim(),
        state: values.state.trim(),
      };

      let response;

      if (isEditMode) {
        if (!id) {
          setToast({
            message: "District ID is missing.",
            open: true,
            severity: "error",
          });
          return;
        }

        response = await updateDistrict(id, payload);
      } else {
        response = await createDistrict(payload);
      }

      navigate("/districts", {
        replace: true,
        state: {
          toast: {
            message:
              response?.message ||
              (isEditMode
                ? "District updated successfully."
                : "District created successfully."),
            severity: "success",
          },
        },
      });
    } catch (apiError) {
      setToast({
        message: extractApiErrorMessage(
          apiError,
          isEditMode ? "Failed to update district." : "Failed to create district."
        ),
        open: true,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MasterShell pageClassName="districts-module">
      {loading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading district</h2>
          <p className="masters-state__text">
            Please wait while the district details are being prepared.
          </p>
        </div>
      ) : (
        <MasterFormPage
          description={
            isEditMode
              ? "Review and update district information using the current application layout."
              : "Add a new district master entry using the existing application design language."
          }
          fields={fields}
          onBack={() => navigate("/districts")}
          onChange={handleChange}
          onSubmit={handleSubmit}
          readOnly={readOnly}
          submitDisabled={false}
          submitLabel={isEditMode ? "Save Changes" : "Save District"}
          submitNote=""
          submitting={submitting}
          title={isEditMode ? "Edit District" : "Add District"}
          values={values}
        />
      )}

      <MasterToast
        message={toast.message}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
